import React, { useState } from "react";
import tw from "twin.macro";
import styled from "styled-components";
import { css } from "styled-components/macro"; //eslint-disable-line
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Clock,
  Wallet,
  CheckCircle2,
  Building2,
  ArrowRight,
  FileText,
  Globe,
} from "lucide-react";
import LandingLayout from "layouts/LandingLayout";
import Hero, { SlantedBackground } from "components/hero/BackgroundAsImage";
import { DESTINATIONS, DestinationItem } from "constants/services";
import CtaFinal from "components/cta/CtaFinal";
import useInView from "helpers/useInView";
import { fadeUpVariants, slideXVariants } from "lib/animations";
import { IMAGES } from "constants/images";

// ─── Design tokens (sync with tailwind.config.js) ────────────────────────────
const C_PRIMARY = "#ff6c00"; // primary-500
const C_SECONDARY = "#111111"; // secondary-500

// ─── Animation ───────────────────────────────────────────────────────────────

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const MDiv = motion.div as any;

const FadeUpSection: React.FC<{
  children: React.ReactNode;
  delay?: number;
}> = ({ children, delay = 0 }) => {
  const [ref, inView] = useInView({ once: true });
  return (
    <MDiv
      ref={ref as React.Ref<HTMLDivElement>}
      variants={fadeUpVariants}
      initial="hidden"
      animate={inView ? "visible" : "hidden"}
      transition={{ delay }}
    >
      {children}
    </MDiv>
  );
};

// ─── Styled ──────────────────────────────────────────────────────────────────

const PageWrapper = tw.div`max-w-screen-xl mx-auto px-6 py-16`;

// ── Carousel ─────────────────────────────────────────────────────────────────

const CarouselSection = styled.div`
  ${tw`mb-12`}
`;

const CarouselHeader = tw.div`flex items-center justify-between mb-6`;
const CarouselLabel = styled.p`
  ${tw`text-xs font-black uppercase tracking-widest text-primary-500`}
`;
const CarouselTitle = tw.h2`text-2xl font-black text-gray-900`;

/** Grille fixe : 4 cartes côte à côte */
const CarouselGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  width: 100%;
`;

const CarouselCard = styled.button<{ $active: boolean }>`
  position: relative;
  overflow: hidden;
  cursor: pointer;
  height: 20rem;
  border: none;
  outline: none;
  padding: 0;
  border-bottom: 4px solid ${(p) => (p.$active ? C_PRIMARY : "transparent")};
  transition: border-color 0.2s ease;

  &:hover .card-bg {
    transform: scale(1.05);
  }

  &::after {
    content: "";
    position: absolute;
    inset: 0;
    background: ${(p) =>
      p.$active ? "rgba(255,108,0,0.18)" : "rgba(0,0,0,0)"};
    transition: background 0.25s ease;
    pointer-events: none;
    z-index: 2;
  }
`;

const CardBg = styled.div<{ $src: string }>`
  position: absolute;
  inset: 0;
  background-image: url(${(p) => p.$src});
  background-size: cover;
  background-position: center;
  transition: transform 0.5s ease;
`;

const CardOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(
    to top,
    rgba(17, 17, 17, 0.92) 0%,
    rgba(17, 17, 17, 0.25) 55%,
    transparent 100%
  );
  z-index: 1;
`;

const CardContent = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 1.25rem 1.5rem;
  text-align: left;
  z-index: 3;
`;

const CardFlag = styled.span`
  font-size: 2rem;
  display: block;
  margin-bottom: 0.35rem;
  line-height: 1;
`;

const CardName = styled.p`
  color: white;
  font-weight: 900;
  font-size: 1rem;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  margin-bottom: 0.2rem;
`;

const CardMeta = styled.p`
  font-size: 0.7rem;
  font-weight: 700;
  color: ${C_PRIMARY};
  display: flex;
  align-items: center;
  gap: 0.25rem;
`;

const ActiveIndicator = styled.div`
  position: absolute;
  top: 1rem;
  right: 1rem;
  width: 8px;
  height: 8px;
  background: ${C_PRIMARY};
  z-index: 4;
`;

// ── Detail panel ─────────────────────────────────────────────────────────────

const DetailPanel = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  border: 1px solid #e5e7eb;
  overflow: hidden;

  @media (min-width: 1024px) {
    grid-template-columns: 5fr 7fr;
  }
`;

const ImageCol = styled.div`
  position: relative;
  overflow: hidden;
  min-height: 30rem;
`;

const DestBg = styled.div<{ $src: string }>`
  position: absolute;
  inset: 0;
  background-image: url(${(p) => p.$src});
  background-size: cover;
  background-position: center;
`;

const DestOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(
    to top,
    rgba(17, 17, 17, 0.9) 0%,
    transparent 50%
  );
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  padding: 2rem;
`;

const DestFlagBig = styled.span`
  font-size: 4rem;
  line-height: 1;
  display: block;
  margin-bottom: 0.5rem;
`;

const DestCountryName = styled.p`
  color: white;
  font-weight: 900;
  font-size: 2.25rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  line-height: 1;
`;

const DestCountryTagline = styled.p`
  color: rgba(255, 255, 255, 0.65);
  font-size: 0.8rem;
  margin-top: 0.4rem;
  display: flex;
  align-items: center;
  gap: 0.35rem;
`;

// Info column

const InfoCol = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 2.5rem;
  background: white;
`;

const InfoHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.5rem;
  padding-bottom: 1.5rem;
  border-bottom: 1px solid #f0f0f0;
`;

const InfoBadge = styled.span`
  font-size: 0.65rem;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  padding: 0.3rem 0.75rem;
  background: #fff4ec;
  color: ${C_PRIMARY};
  border-left: 2px solid ${C_PRIMARY};
`;

const DestDesc = styled.p`
  color: #6b7280;
  line-height: 1.75;
  margin-bottom: 1.75rem;
  font-size: 0.95rem;
`;

const MetaGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  border: 1px solid #f0f0f0;
  margin-bottom: 1.75rem;
`;

const MetaCell = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 1.1rem 1.25rem;
  border-right: 1px solid #f0f0f0;

  &:last-child {
    border-right: none;
  }
`;

const MetaIcon = styled.div<{ $color: string }>`
  flex-shrink: 0;
  color: ${(p) => p.$color};
  margin-top: 2px;
`;

const MetaLabel = styled.p`
  font-size: 0.65rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: #9ca3af;
  margin-bottom: 0.2rem;
`;

const MetaValue = styled.p`
  font-size: 0.95rem;
  font-weight: 900;
  color: ${C_SECONDARY};
`;

const TwoColSection = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;
  margin-bottom: 1.75rem;
`;

const SectionBlock = styled.div``;

const SectionLabel = styled.p`
  font-size: 0.65rem;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: #9ca3af;
  margin-bottom: 0.6rem;
`;

const ReqList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
`;

const ReqItem = styled.li`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.8rem;
  color: #374151;

  .req-icon {
    color: ${C_PRIMARY};
    flex-shrink: 0;
  }
`;

const UnivList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
`;

const UnivItem = styled.li`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.8rem;
  color: #374151;

  .univ-icon {
    color: ${C_PRIMARY};
    flex-shrink: 0;
  }
`;

const DestCTA = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem 2rem;
  font-weight: 900;
  color: white;
  text-decoration: none;
  background: ${C_PRIMARY};
  transition:
    background 0.25s ease,
    gap 0.25s ease;
  align-self: flex-start;
  margin-top: 0.25rem;

  &:hover {
    background: ${C_SECONDARY};
    gap: 1.25rem;
  }
`;

// ─── Component ────────────────────────────────────────────────────────────────

const DestinationsPage: React.FC = () => {
  const [activeCode, setActiveCode] = useState<string>("FR");
  const [direction, setDirection] = useState<number>(1);

  const dest: DestinationItem =
    DESTINATIONS.find((d) => d.code === activeCode) || DESTINATIONS[0];

  const handleSelect = (code: string) => {
    const prevIdx = DESTINATIONS.findIndex((d) => d.code === activeCode);
    const nextIdx = DESTINATIONS.findIndex((d) => d.code === code);
    setDirection(nextIdx >= prevIdx ? 1 : -1);
    setActiveCode(code);
  };

  return (
    <LandingLayout disableAnimation>
      <Hero
        backgroundImageSrc={IMAGES.travelImg}
        notification="Destinations"
        heading={
          <>
            <span>Où souhaitez-vous</span>
            <br />
            <SlantedBackground>étudier ?</SlantedBackground>
          </>
        }
        description="France, Belgique, Canada, Luxembourg — choisissez votre destination et découvrez toutes les informations pour réussir votre mobilité internationale."
      />

      <PageWrapper>
        {/* ── 1. Carousel (en haut) ── */}
        <FadeUpSection>
          <CarouselSection>
            <CarouselHeader>
              <div>
                <CarouselLabel>Choisissez une destination</CarouselLabel>
                <CarouselTitle>Nos destinations</CarouselTitle>
              </div>
            </CarouselHeader>

            <CarouselGrid>
              {DESTINATIONS.map((d) => (
                <CarouselCard
                  key={d.code}
                  $active={activeCode === d.code}
                  onClick={() => handleSelect(d.code)}
                  aria-label={`Sélectionner ${d.name}`}
                >
                  <CardBg className="card-bg" $src={d.imageSrc} />
                  <CardOverlay />
                  {activeCode === d.code && <ActiveIndicator />}
                  <CardContent>
                    <CardFlag>{d.flag}</CardFlag>
                    <CardName>{d.name}</CardName>
                    <CardMeta>
                      <Clock size={10} />
                      {d.processingTime}
                    </CardMeta>
                  </CardContent>
                </CarouselCard>
              ))}
            </CarouselGrid>
          </CarouselSection>
        </FadeUpSection>

        {/* ── 2. Detail panel (en bas) ── */}
        <FadeUpSection delay={0.1}>
          <DetailPanel>
            {/* Colonne image */}
            <ImageCol>
              <AnimatePresence initial={false} custom={direction} mode="wait">
                <MDiv
                  key={dest.code + "-bg"}
                  custom={direction}
                  variants={{
                    enter: (d: number) => ({ opacity: 0 }),
                    center: { opacity: 1, transition: { duration: 0.45 } },
                    exit: (d: number) => ({
                      opacity: 0,
                      transition: { duration: 0.25 },
                    }),
                  }}
                  initial="enter"
                  animate="center"
                  exit="exit"
                  style={{ position: "absolute", inset: 0 }}
                >
                  <DestBg $src={dest.imageSrc} />
                  <DestOverlay>
                    <DestFlagBig>{dest.flag}</DestFlagBig>
                    <DestCountryName>{dest.name}</DestCountryName>
                    <DestCountryTagline>
                      <Globe size={13} />
                      {dest.tagline}
                    </DestCountryTagline>
                  </DestOverlay>
                </MDiv>
              </AnimatePresence>
            </ImageCol>

            {/* Colonne infos */}
            <AnimatePresence initial={false} custom={direction} mode="wait">
              <MDiv
                key={dest.code + "-info"}
                custom={direction}
                variants={slideXVariants}
                initial="enter"
                animate="center"
                exit="exit"
              >
                <InfoCol>
                  <InfoHeader>
                    <InfoBadge>Destination MomsEduc</InfoBadge>
                    <InfoBadge>Dossier disponible</InfoBadge>
                  </InfoHeader>

                  <DestDesc>{dest.description}</DestDesc>

                  <MetaGrid>
                    <MetaCell>
                      <MetaIcon $color={C_PRIMARY}>
                        <Clock size={18} />
                      </MetaIcon>
                      <div>
                        <MetaLabel>Délai de traitement</MetaLabel>
                        <MetaValue>{dest.processingTime}</MetaValue>
                      </div>
                    </MetaCell>
                    <MetaCell>
                      <MetaIcon $color={C_PRIMARY}>
                        <Wallet size={18} />
                      </MetaIcon>
                      <div>
                        <MetaLabel>Budget mensuel moyen</MetaLabel>
                        <MetaValue>{dest.averageCost}</MetaValue>
                      </div>
                    </MetaCell>
                  </MetaGrid>

                  <TwoColSection>
                    <SectionBlock>
                      <SectionLabel>Conditions requises</SectionLabel>
                      <ReqList>
                        {dest.requirements.map((req) => (
                          <ReqItem key={req}>
                            <CheckCircle2 size={13} className="req-icon" />
                            {req}
                          </ReqItem>
                        ))}
                      </ReqList>
                    </SectionBlock>

                    <SectionBlock>
                      <SectionLabel>Universités partenaires</SectionLabel>
                      <UnivList>
                        {dest.universities.map((u) => (
                          <UnivItem key={u}>
                            <Building2 size={13} className="univ-icon" />
                            {u}
                          </UnivItem>
                        ))}
                      </UnivList>
                    </SectionBlock>
                  </TwoColSection>

                  <DestCTA to="/contact">
                    <FileText size={15} />
                    Commencer mon dossier pour {dest.name}
                    <ArrowRight size={15} />
                  </DestCTA>
                </InfoCol>
              </MDiv>
            </AnimatePresence>
          </DetailPanel>
        </FadeUpSection>
      </PageWrapper>

      {/* ── CTA Final ── */}
      <CtaFinal
        label="Votre destination vous attend"
        title="Prêt à démarrer votre aventure académique ?"
        description="Nos conseillers connaissent chaque destination sur le bout des doigts. Obtenez votre AVI et votre visa en un temps record."
      />
    </LandingLayout>
  );
};

export default DestinationsPage;
